import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const VideoContext = createContext();

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) throw new Error('useVideo must be used within a VideoProvider');
  return context;
};

export const VideoProvider = ({ children }) => {
  const [videos, setVideos] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [visualStyles, setVisualStyles] = useState([]);
  const [videoTypes, setVideoTypes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: videosData, error: videosError } = await supabase
        .from('videos')
        .select('*')
        .order('created_at', { ascending: false });
      if (videosError) throw videosError;

      const { data: companiesData, error: companiesError } = await supabase
        .from('production_companies')
        .select('*')
        .order('overall_rating', { ascending: false });
      if (companiesError) throw companiesError;

      const { data: metaData, error: metaError } = await supabase
        .from('metadata')
        .select('*');
      if (metaError) throw metaError;

      if (videosData) setVideos(videosData);
      if (companiesData) setCompanies(companiesData);

      if (metaData) {
        setIndustries(metaData.find(m => m.key === 'industries')?.options || []);
        setVisualStyles(metaData.find(m => m.key === 'visual_styles')?.options || []);
        setVideoTypes(metaData.find(m => m.key === 'video_types')?.options || []);
      }

      if (videosData) {
        const tagCounts = {};
        videosData.forEach(v => {
          v.tags?.forEach(tag => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        });
        setTags(Object.entries(tagCounts).map(([name, count]) => ({ name, count })));
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const addVideo = async (video) => {
    const payload = {
      ...video,
      audience: video.audience || null,
      why_it_works: video.why_it_works || null,
    };
    const { data, error } = await supabase
      .from('videos')
      .insert([payload])
      .select();
    if (!error) fetchData();
    return { data, error };
  };

  const addVideos = async (videosArray) => {
    const payload = videosArray.map(v => ({
      ...v,
      audience: v.audience || null,
      why_it_works: v.why_it_works || null,
    }));
    const { data, error } = await supabase
      .from('videos')
      .insert(payload)
      .select();
    if (!error) fetchData();
    return { data, error };
  };

  const updateVideo = async (id, video) => {
    const payload = {
      ...video,
      audience: video.audience || null,
      why_it_works: video.why_it_works || null,
    };
    const { error } = await supabase
      .from('videos')
      .update(payload)
      .eq('id', id);
    if (!error) fetchData();
  };

  const deleteVideo = async (id) => {
    const { error } = await supabase
      .from('videos')
      .delete()
      .eq('id', id);
    if (!error) fetchData();
  };

  const addCompany = async (company) => {
    const { data, error } = await supabase
      .from('production_companies')
      .insert([company])
      .select();
    if (!error) fetchData();
    return { data, error };
  };

  const updateCompany = async (id, company) => {
    const { error } = await supabase
      .from('production_companies')
      .update(company)
      .eq('id', id);
    if (!error) fetchData();
  };

  const deleteCompany = async (id) => {
    const { error } = await supabase
      .from('production_companies')
      .delete()
      .eq('id', id);
    if (!error) fetchData();
  };

  const saveMetadata = async (key, options) => {
    const { error } = await supabase
      .from('metadata')
      .update({ options })
      .eq('key', key);
    if (!error) fetchData();
  };

  const incrementViews = async (id) => {
    setVideos(prev => prev.map(v =>
      v.id === id ? { ...v, views: (v.views || 0) + 1 } : v
    ));
    try {
      await supabase.rpc('increment_video_views', { p_video_id: id });
    } catch (err) {
      console.error("Failed to increment views:", err);
    }
  };

  return (
    <VideoContext.Provider value={{
      videos,
      companies,
      industries,
      visualStyles,
      videoTypes,
      tags,
      loading,
      error,
      activeVideoId,
      setActiveVideoId,
      addVideo,
      addVideos,
      updateVideo,
      deleteVideo,
      addCompany,
      updateCompany,
      deleteCompany,
      incrementViews,
      saveIndustries: (opts) => saveMetadata('industries', opts),
      saveVisualStyles: (opts) => saveMetadata('visual_styles', opts),
      saveVideoTypes: (opts) => saveMetadata('video_types', opts),
    }}>
      {children}
    </VideoContext.Provider>
  );
};